import { QueryClient } from "@tanstack/vue-query";

import i18n from "@/i18n";
import store from "@/store";

import type { AxiosError } from "axios";

type ErrorData = {
  auth_link?: string;
  message: string;
  errors: Record<string, Array<string> | string>;
};

const showErrorMessage = (message: string) => {
  store.commit("setGlobalMessage", {
    title: message,
    type: "error",
  });
};

const extractAllErrors = (
  errors: Record<string, Array<string> | string>
): string[] => {
  const allErrors: string[] = [];

  Object.values(errors).forEach((errorValue) => {
    if (typeof errorValue === "string") {
      allErrors.push(errorValue);
    } else if (Array.isArray(errorValue)) {
      allErrors.push(...errorValue);
    }
  });

  return allErrors;
};

const isAxiosError = (error: unknown): error is AxiosError<ErrorData> => {
  return error instanceof Error && "response" in error;
};

const isZodError = (error: unknown): boolean => {
  return (
    error instanceof Error && error.name === "ZodError" && "issues" in error
  );
};

const handleQueryError = (error: unknown) => {
  if (isZodError(error)) {
    return;
  }

  if (!isAxiosError(error)) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.global.t("COMMON.ERRORS.GENERAL_ERROR");
    showErrorMessage(message);
    return;
  }

  const { data, status } = error.response ?? {};

  if (status === 401 && data?.auth_link) {
    return;
  }

  if (data?.message) {
    showErrorMessage(data.message);
    return;
  }

  if (data?.errors) {
    const allErrors = extractAllErrors(data.errors);

    if (allErrors.length) {
      allErrors.forEach(showErrorMessage);
      return;
    }
  }

  showErrorMessage(i18n.global.t("COMMON.ERRORS.GENERAL_ERROR"));
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const axiosError = error as AxiosError;
        if (
          axiosError.response?.status === 401 ||
          axiosError.response?.status === 403
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
      onError: handleQueryError,
    },
  },
});

queryClient.setQueryDefaults([], {
  onError: handleQueryError,
});
